import useIsDcxMarket from '@/hooks/useIsDcxMarket';
import externalLinkService from '@/services/app/externalLinkService';
import userBrowserService from '@/services/user/userBrowserService';

import {resolveSsoTemplateFlowUsesBatch} from './ssoLinkMode';
import useSsoLinkBatch from './useSsoLinkBatch';
import useSsoLinkSingle from './useSsoLinkSingle';

export interface UseSsoLinkParams {
    ssoTemplate?: string;
    ssoParams?: Record<string, string | undefined>;
    disabled?: boolean;
    openInBluefy?: boolean;
    queryStringTemplate?: string;
    linkProcessor?: (url: string) => string;
    deviceType?: string;
    deviceSerialNumber?: string;
}

export interface UseSsoLinkResult {
    redirect: () => void | Promise<void>;
    link?: string;
}

/**
 * DCX-aware SSO hook: batch CreateLoginTokenList on DCX markets, single CreateLoginToken otherwise.
 * Use only when opening an SSO template link (not externalLinkCode-only flows).
 */
const useSsoLink = ({
    ssoTemplate,
    ssoParams,
    disabled,
    openInBluefy,
    queryStringTemplate,
    linkProcessor,
    deviceType,
    deviceSerialNumber,
}: UseSsoLinkParams): UseSsoLinkResult => {
    const isDcx = useIsDcxMarket();
    const useBatch = resolveSsoTemplateFlowUsesBatch(isDcx);

    const singleSso = useSsoLinkSingle({
        ssoTemplate,
        ssoParams,
        disabled: disabled || useBatch,
        openInBluefy,
        queryStringTemplate,
        linkProcessor,
    });

    const batch = useSsoLinkBatch({
        ssoTemplate,
        url: undefined,
        params: ssoParams,
        disabled: disabled || !useBatch,
        queryStringTemplate,
        openInBluefy,
        linkProcessor,
        deviceType,
        deviceSerialNumber,
    });

    if (useBatch) {
        return {redirect: batch.redirect, link: batch.link};
    }

    const isIos = userBrowserService.isIosDevice();

    const redirect = () => {
        if (singleSso.preferSsoLinkInNewTab && singleSso.link) {
            externalLinkService.openResolvedUrl({
                url: singleSso.link,
                openInBluefy: false,
                ssoTemplate,
                externalLink: undefined,
                ssoParams,
                linkProcessor,
                deviceType,
                deviceSerialNumber,
            });
            return;
        }
        externalLinkService.openExternalLink({
            ssoTemplate,
            externalLink: undefined,
            externalLinkCode: undefined,
            openInBluefy: openInBluefy && isIos,
            linkProcessor,
            ssoParams,
            link: singleSso.link,
            deviceType,
            deviceSerialNumber,
        });
    };

    return {redirect, link: singleSso.link};
};

export default useSsoLink;
